/* eslint-disable @typescript-eslint/naming-convention */
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag, Size } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import type { RegulatoryHeader } from '@ps/web-martech-cms-types';
import { useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import sharedStyles from '../../../../styles/shared.scss';
import PanicButtonVegas from '../PanicButtonVegas/PanicButtonVegas';

import styles from './TopHeader.scss';
import RegulatoryHeaderCAON from './components/RegulatoryHeaderCAON/RegulatoryHeaderCAON';
import RegulatoryHeaderCH from './components/RegulatoryHeaderCH/RegulatoryHeaderCH';
import RegulatoryHeaderDE from './components/RegulatoryHeaderDE/RegulatoryHeaderDE';
import RegulatoryHeaderDESH from './components/RegulatoryHeaderDESH/RegulatoryHeaderDESH';
import RegulatoryHeaderFR from './components/RegulatoryHeaderFR/RegulatoryHeaderFR';
import RegulatoryHeaderIT from './components/RegulatoryHeaderIT/RegulatoryHeaderIT';
import RegulatoryHeaderUK from './components/RegulatoryHeaderUK/RegulatoryHeaderUK';
import SessionHeader from './components/SessionHeader/SessionHeader';

export interface Props {
  regulatory_header: RegulatoryHeader[];
}

type ComponentProps = Props & { index: number };

const TopHeader = forwardRef<HTMLDivElement, Props>(({ regulatory_header }, ref) => {
  const site = useSelector(select.cms.getSiteId);
  const isSiteUS = useSelector(select.cms.isSiteUS);
  const isMobile = useSelector(select.device.isMobile);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const DEPanic = useFeature(FeatureFlag.DEPanic);

  const { greaterThan } = useScreenSize();

  const components = useMemo(
    () =>
      [
        {
          condition:
            [
              Sites.DEN,
              Sites.DE_PS_EXT,
              Sites.POR,
              Sites.PT_PS_EXT,
              Sites.SWE,
              Sites.SE_PS_EXT,
              Sites.EST,
              Sites.ES_PS_EXT,
              Sites.BEL,
              Sites.BE_PS_EXT,
              Sites.NL,
              Sites.NL_PS_EXT,
              Sites.CZE,
              Sites.CH_PS_EXT,
              Sites.IT_SNAI_EXT
            ].includes(site) || isSiteUS,
          render: ({ index, ...rest }: ComponentProps) => <SessionHeader key={index} {...rest} />
        },
        {
          condition: site === Sites.ITA,
          render: ({ index, ...rest }: ComponentProps) => (
            <RegulatoryHeaderIT key={index} {...rest} />
          )
        },
        {
          condition: site === Sites.CH,
          render: ({ index, ...rest }: ComponentProps) => (
            <RegulatoryHeaderCH key={index} {...rest} />
          )
        },
        {
          condition: site === Sites.DE,
          render: ({ index, ...rest }: ComponentProps) => (
            <RegulatoryHeaderDE key={index} {...rest} />
          )
        },
        {
          condition: site === Sites.DE_SH,
          render: ({ index, ...rest }: ComponentProps) => (
            <RegulatoryHeaderDESH key={index} {...rest} />
          )
        },
        {
          condition: site === Sites.FRA,
          render: ({ index, ...rest }: ComponentProps) => (
            <RegulatoryHeaderFR key={index} {...rest} />
          )
        },
        {
          condition: !isMobile && site === Sites.UK,
          render: ({ index, ...rest }: ComponentProps) => (
            <RegulatoryHeaderUK key={index} {...rest} />
          )
        },
        {
          condition: (isMobile || !greaterThan(Size.Small)) && DEPanic && isAuthenticated,
          render: ({ index }: ComponentProps) => <PanicButtonVegas key={index} />
        },
        {
          condition: site === Sites.CA_ON,
          render: ({ index, ...rest }: ComponentProps) => (
            <RegulatoryHeaderCAON key={index} {...rest} />
          )
        }
      ].filter(({ condition }) => condition),
    [site, isSiteUS, isMobile, greaterThan, DEPanic, isAuthenticated]
  );

  return components.length ? (
    <div
      ref={ref}
      className={classNames(sharedStyles.headerWidgetInner, styles.topHeader, {
        [styles.paddingRestriction]: site === Sites.SWE,
        [styles.multilineHeader]: site === Sites.SWE,
        [styles.reorderHeader]: [Sites.US_PA, Sites.USMI].includes(site)
      })}
    >
      {components.map(({ render }, index) => render({ index, regulatory_header }))}
    </div>
  ) : null;
});

export default TopHeader;
